<script setup lang="ts">
import { generateSkuCode, reorderByIds } from "core/helpers";
import { LOCALE } from "core/interfaces";

import { getKindPathMapping } from "~/common/helpers/get-kind-path-mapping";
import type { CartItem } from "~/common/interfaces/cart-item.interface";
import { useCartStore } from "~/stores/cart";
import { useModalStore } from "~/stores/modal";
import { useShopStore } from "~/stores/shop";

const config = useRuntimeConfig();
const { t, locale } = useI18n();
const route = useRoute();
const shopStore = useShopStore();
const { cartModal } = useModalStore();
const cartStore = useCartStore();
const { validateRoute } = useKind();

const id = +route.params.product;

const { data: currentProduct } = await useAsyncData(`/shop/products/${id}`, () => shopStore.getProductById(id));
if (currentProduct.value) {
	shopStore.currentProduct = currentProduct.value;
	shopStore.addToRecentlyViewed(currentProduct.value);
}

validateRoute();

await useAsyncData("/shop/products?ids", () => shopStore.getRecentlyViewedProducts());

const primaryProductImageUrl = computed(() => `${config.public.baseUrl}/images/${shopStore.currentProduct.imageIds[0]}`);
const kindPathMapping = computed(() => getKindPathMapping(locale.value as LOCALE, shopStore.components.kinds));
const primaryProductData = computed(() => {
	const { currentProduct: product } = shopStore;
	const reorderedMaterials = reorderByIds(product.materials, product.feature.materialOrderIds);
	const kindId = product.kind.id;
	const materialId = reorderedMaterials[0].id;
	const sizeId = product?.sizes[0].id;
	const pricePolicy = shopStore.components?.pricePolicies.find(pricePolicy => pricePolicy.kind === kindId &&
			pricePolicy.materials.includes(materialId) &&
			pricePolicy.sizes.includes(sizeId));

	return {
		id: product.id,
		materialId,
		sizeId,
		sku: generateSkuCode(product.id, materialId, sizeId),
		price: pricePolicy?.price
	};
});

useSeoMeta({
	title: () => t("meta.shopProduct.title", { productName: shopStore.currentProduct.name }),
	ogTitle: () => t("meta.shopProduct.ogTitle", { productName: shopStore.currentProduct.name }),
	description: () => shopStore.currentProduct.description,
	ogDescription: () => shopStore.currentProduct.description,
	ogImage: () => primaryProductImageUrl.value
});

useSchemaOrg([
	defineProduct({
		name: () => shopStore.currentProduct.name,
		description: () => shopStore.currentProduct.description,
		image: () => primaryProductImageUrl.value,
		sku: () => primaryProductData.value.sku,
		offers: () => [{ price: primaryProductData.value.price, priceCurrency: "UAH" }]
	})
]);
</script>

<template>
	<div class="px-6 pt-4 pb-[144px] h-full sm:pb-11 xs:px-4 xs:pb-11">
		<article class="flex max-w-[1024px] m-auto gap-x-10 sm:gap-x-6 xs:flex-col">
			<AppBreadcrumbs
				class="hidden xs:block mb-4"
				:replace-params="false"
				:renamed-paths="{
					shop: $t('shop'),
					...kindPathMapping
				}"
			/>
			<div class="w-[45%] xs:w-full">
				<CommonSwiper
					:image-ids="shopStore.currentProduct?.imageIds"
					:tags="shopStore.currentProduct?.tags"
					show-thumbnails
				/>
			</div>
			<div class="w-[55%] xs:w-full">
				<AppBreadcrumbs
					class="xs:hidden"
					:replace-params="false"
					:renamed-paths="{
						shop: $t('shop'),
						...kindPathMapping
					}"
				/>
				<ProductDetails
					class="mt-6"
					:product="shopStore.currentProduct"
					:price-policies="shopStore.components?.pricePolicies"
					@add-item-to-cart="(i) => {
						cartStore.addItemToCart(i as CartItem);
						cartModal.openModal();
					}"
				/>
			</div>
		</article>
		<div v-if="shopStore.recentlyViewedProductIds.length" class="mt-[128px] max-w-[1400px] m-auto xs:mt-11">
			<ShopRecentList />
		</div>
	</div>
</template>
